import { useState, useEffect, useCallback } from "react";

/**
 * Custom hook to fetch artists associated with a specific bar with auto-refresh
 * @param {number} barId - The bar ID to fetch artists for
 * @param {object} options - Options for configuration (autoRefresh)
 * @returns {object} - { artists, loading, error, refetch, addArtist, removeArtist }
 */
const useBarArtists = (barId, options = {}) => {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { autoRefresh = true } = options;

  const fetchArtists = useCallback(async () => {
    if (!barId) {
      setArtists([]);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`/api/artists/bar/${barId}`, {
        credentials: "include",
      });

      const data = await response.json();

      if (data.success) {
        setArtists(data.artists || []);
        setError(null);
      } else {
        setError(data.error || "Failed to fetch artists");
      }
    } catch (err) {
      console.error("Error fetching bar artists:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [barId]);

  // Add artist to bar
  const addArtist = async (artistId) => {
    if (!barId || !artistId) return { success: false, error: "Invalid data" };

    try {
      const response = await fetch(`/api/artists/bar/${barId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ artist_id: artistId }),
      });

      const data = await response.json();

      if (data.success) {
        // Refresh artists list
        await fetchArtists();
        return { success: true, artist: data.artist };
      } else {
        return { success: false, error: data.error };
      }
    } catch (err) {
      console.error("Error adding artist to bar:", err);
      return { success: false, error: err.message };
    }
  };

  // Remove artist from bar
  const removeArtist = async (artistId) => {
    if (!barId || !artistId) return { success: false, error: "Invalid data" };

    try {
      const response = await fetch(`/api/artists/bar/${barId}/${artistId}`, {
        method: "DELETE",
        credentials: "include",
      });

      const data = await response.json();

      if (data.success) {
        // Refresh artists list
        await fetchArtists();
        return { success: true };
      } else {
        return { success: false, error: data.error };
      }
    } catch (err) {
      console.error("Error removing artist from bar:", err);
      return { success: false, error: err.message };
    }
  };

  // Fetch on mount and when dependencies change
  useEffect(() => {
    fetchArtists();
  }, [fetchArtists]);

  // Auto-refresh every 2 minutes
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchArtists();
    }, 120000); // 120 seconds (2 minutes)

    return () => clearInterval(interval);
  }, [fetchArtists, autoRefresh]);

  return {
    artists,
    loading,
    error,
    refetch: fetchArtists,
    addArtist,
    removeArtist,
  };
};

export default useBarArtists;
